import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaEye,
  FaPlay,
  FaSpinner,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock,
  FaUser,
  FaBuilding,
  FaUsers,
  FaCheckCircle,
  FaTimes,
  FaSearch,
  FaFilter,
  FaYoutube,
  FaExternalLinkAlt,
  FaMicrophone,
  FaSync
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const RunningFreeConference = () => {
  const [conferences, setConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [currentScreenshot, setCurrentScreenshot] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedConference, setSelectedConference] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showProofModal, setShowProofModal] = useState(false);
  const [selectedProof, setSelectedProof] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [selectedReporter, setSelectedReporter] = useState(null);

  // Search/filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [todayOnly, setTodayOnly] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 15;

  // Set startDate and endDate to today when todayOnly is checked
  useEffect(() => {
    if (todayOnly) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayStr = `${yyyy}-${mm}-${dd}`;
      setStartDate(todayStr);
      setEndDate(todayStr);
    }
  }, [todayOnly]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, startDate, endDate, todayOnly]);

  // Date helper function
  const toDate = (dateStr, endOfDay = false) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    if (endOfDay) {
      d.setHours(23, 59, 59, 999);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    return d;
  };

  // Function to filter conferences based on search text and date filters
  const getFilteredConferences = () => {
    const q = searchText.trim().toLowerCase();
    const filtered = conferences.filter((conference) => {
      // Search filter
      if (q) {
        // Check conferenceId
        if (conference.conferenceId && conference.conferenceId.toString().trim().toLowerCase().includes(q)) return true;
        // Check topic
        if (conference.topic && conference.topic.trim().toLowerCase().includes(q)) return true;
        // Check submitter name
        if (conference.submittedBy?.name && conference.submittedBy.name.trim().toLowerCase().includes(q)) return true;
        // Check all relevant fields in accepted reporters
        if (
          conference.acceptedReporters &&
          conference.acceptedReporters.some((reporter) => {
            const iinsafId = reporter.iinsafId ? reporter.iinsafId.toString().trim().toLowerCase() : "";
            const reporterName = reporter.reporterName ? reporter.reporterName.trim().toLowerCase() : "";
            const reporterEmail = reporter.reporterEmail ? reporter.reporterEmail.trim().toLowerCase() : "";
            return (
              iinsafId.includes(q) ||
              reporterName.includes(q) ||
              reporterEmail.includes(q)
            );
          })
        ) {
          return true;
        }
        return false;
      }

      // Date filter
      const conferenceDate = toDate(conference.conferenceDate);
      const filterStart = toDate(startDate);
      const filterEnd = toDate(endDate, true);

      // Skip conferences with invalid dates if filters are active
      if ((todayOnly || filterStart || filterEnd) && !conferenceDate) {
        return false;
      }

      // Apply date filters
      if (filterStart && conferenceDate < filterStart) return false;
      if (filterEnd && conferenceDate > filterEnd) return false;

      return true;
    });
    console.log("Search term:", searchText, "Filtered conferences:", filtered);
    return filtered;
  };

  // Pagination logic
  const filteredConferences = getFilteredConferences();
  const totalPages = Math.ceil(filteredConferences.length / rowsPerPage);
  const paginatedConferences = filteredConferences.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    const fetchRunningConferences = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/admin/get/running/conferences`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched Running Conferences Data:", response);

        const sortedConferences = (response.data.data || []).sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : (a.conferenceDate ? new Date(a.conferenceDate) : new Date(0));
          const dateB = b.createdAt ? new Date(b.createdAt) : (b.conferenceDate ? new Date(b.conferenceDate) : new Date(0));
          return dateB - dateA;
        });
        setConferences(sortedConferences);
      } catch (err) {
        setError("Failed to fetch running conferences");
      } finally {
        setLoading(false);
      }
    };
    fetchRunningConferences();
  }, []);

  // View conference details with reporters
  const handleViewDetails = async (conference) => {
    try {
      const token = localStorage.getItem("token");
      console.log("View details token:", token);
      console.log("Conference ID:", conference.conferenceId);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/admin/get/conference/${conference.conferenceId}/reporters`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Conference details response:", response.data);
      if (response.data.success) {
        setSelectedConference(response.data.data);
        setShowModal(true);
      }
    } catch (error) {
      console.error("Error fetching conference details:", error);
      console.error("Error response:", error.response?.data);
    }
  };

  const handleViewProof = (conference) => {
    setSelectedProof(conference);
    setShowProofModal(true);
  };

  const handleApproveProof = async (conferenceId, reporterId) => {
    try {
      setActionLoading(true);
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/approve/conference/proof/${conferenceId}/${reporterId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        // Refresh the conferences list
        window.location.reload();
        setShowProofModal(false);
        toast.success("Proof approved successfully!");
      }
    } catch (error) {
      console.error("Error approving proof:", error);
      toast.error("Failed to approve proof");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectProof = async (conferenceId, reporterId) => {
    try {
      setActionLoading(true);
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/reject/conference/proof/${conferenceId}/${reporterId}`,
        { adminRejectNote: rejectNote },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.success) {
        // Refresh the conferences list
        window.location.reload();
        setShowProofModal(false);
        setShowRejectModal(false);
        setRejectNote("");
        setSelectedReporter(null);
        toast.success("Proof rejected successfully!");
      }
    } catch (error) {
      console.error("Error rejecting proof:", error);
      toast.error("Failed to reject proof");
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectModal = (conferenceId, reporterId, reporterName) => {
    setSelectedReporter({ conferenceId, reporterId, reporterName });
    setShowRejectModal(true);
  };



  return (
    <>

      {/* Search and Filter Controls */}
      <div className="mb-6 space-y-4">
        {/* Search Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="relative w-full sm:w-96">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="w-5 h-5 text-gray-500" />
            </div>
            <input
              type="text"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Search by Conference ID, Topic, or Reporter Name..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
          </div>
          <button
            onClick={() => {
              setSearchText("");
              setStartDate("");
              setEndDate("");
              setTodayOnly(false);
            }}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Clear Filters
          </button>
        </div>

        {/* Date Filters */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="todayOnly"
              checked={todayOnly}
              onChange={(e) => setTodayOnly(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="todayOnly" className="text-sm font-medium text-gray-700">
              Today Only
            </label>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">From:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={todayOnly}
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">To:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={todayOnly}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
          Running Free Conferences: {filteredConferences.length}
        </h2>

        <div className="flex flex-wrap gap-2">
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/free-conference/pending")}
          >
            Pending
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-green-500 text-white rounded hover:bg-green-600 font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/free-conference/approved")}
          >
            Approved
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-red-500 text-white rounded hover:bg-red-600 font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/free-conference?status=rejected")}
          >
            Rejected
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-orange-500 text-white rounded hover:bg-orange-600 font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/free-conference/modified")}
          >
            Modified
          </button>
          <button
            className="flex-1 sm:flex-none px-3 py-2 sm:px-4 sm:py-2 bg-purple-500 text-white rounded hover:bg-purple-600 font-semibold transition-colors text-sm sm:text-base min-w-[80px]"
            onClick={() => navigate("/admin/press-conference/free-conference?status=completed")}
          >
            Completed
          </button>
        </div>
      </div>

      {/* Conference Table Design */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : conferences.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <FaMicrophone className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700">
            No Running Conferences
          </h3>
          <p className="text-gray-500">
            You don't have any running conferences at the moment.
          </p>
        </div>
      ) : paginatedConferences.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <FaMicrophone className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700">
            No Results Found
          </h3>
          <p className="text-gray-500">
            {filteredConferences.length === 0
              ? "No conferences match your search criteria."
              : "No conferences on this page."
            }
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-xl shadow-sm border border-gray-100">
            <thead>
              <tr className="bg-gray-100 text-sm text-gray-700">
                <th className="px-4 py-3 text-left">Conference ID</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Topic</th>
                <th className="px-4 py-3 text-left">Purpose</th>
                <th className="px-4 py-3 text-left">Date & Time</th>
                <th className="px-4 py-3 text-left">Location</th>
                <th className="px-4 py-3 text-left">Proof Status</th>
                <th className="px-4 py-3 text-left">Accepted At</th>
                <th className="px-4 py-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedConferences.map((conference) => (
                <tr key={conference._id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-sm">{conference.conferenceId}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${conference.status === "modified"
                      ? "bg-purple-100 text-purple-800"
                      : "bg-blue-100 text-blue-800"
                      }`}>
                      {conference.status === "modified" ? (
                        <>
                          <FaSync className="w-3 h-3 mr-1" />
                          Modified
                        </>
                      ) : (
                        <>
                          <FaCheckCircle className="w-3 h-3 mr-1" />
                          Approved
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">{conference.topic}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                    {conference.purpose}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm">
                      <FaCalendarAlt className="mr-1 text-blue-500" />
                      {conference.conferenceDate}
                    </div>
                    <div className="flex items-center text-xs text-gray-500">
                      <FaClock className="mr-1" />
                      {conference.conferenceTime} {conference.timePeriod}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm">
                      <FaMapMarkerAlt className="mr-1 text-green-500" />
                      {conference.city}, {conference.state}
                    </div>
                    <div className="text-xs text-gray-500">{conference.place}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center text-sm">
                      <FaCheckCircle className="mr-1 text-green-500" />
                      {conference.totalProofSubmitted || 0} submitted
                    </div>
                    <div className="text-xs text-gray-500">
                      {conference.totalAccepted - conference.totalProofSubmitted || 0} pending
                    </div>
                    {conference.acceptedReporters?.some(reporter => reporter.proofDetails?.adminRejectNote) && (
                      <div className="text-xs text-red-500 mt-1">
                        {conference.acceptedReporters.filter(reporter => reporter.proofDetails?.adminRejectNote).length} rejected
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {conference.acceptedReporters && conference.acceptedReporters.length > 0
                      ? new Date(conference.acceptedReporters[0].acceptedAt).toLocaleDateString('en-IN')
                      : "N/A"
                    }
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewDetails(conference)}
                        className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm flex items-center"
                      >
                        <FaEye className="mr-1" />
                        View Details
                      </button>
                      {conference.totalProofSubmitted > 0 && (
                        <button
                          onClick={() => handleViewProof(conference)}
                          className="px-3 py-1 bg-purple-500 text-white rounded-md hover:bg-purple-600 text-sm flex items-center"
                        >
                          <FaCheckCircle className="mr-1" />
                          Review Proofs
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-4 sm:space-y-0">
          {/* Page Info */}
          <div className="text-sm text-gray-600">
            Showing {((currentPage - 1) * rowsPerPage) + 1} to {Math.min(currentPage * rowsPerPage, filteredConferences.length)} of {filteredConferences.length} conferences
          </div>

          {/* Pagination Navigation */}
          <nav className="inline-flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded border border-blue-400 text-blue-800 bg-white hover:bg-blue-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Previous
            </button>

            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 rounded border transition-colors ${currentPage === pageNum
                      ? 'border-blue-400 bg-blue-300 text-blue-900 font-bold'
                      : 'border-blue-400 text-blue-800 bg-white hover:bg-blue-100'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded border border-blue-400 text-blue-800 bg-white hover:bg-blue-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Next
            </button>
          </nav>
        </div>
      )}

      {/* Conference Details Modal */}
      {showModal && selectedConference && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Conference Details - {selectedConference.conferenceId}
                  </h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes className="w-6 h-6" />
                  </button>
                </div>

                {/* Conference Information */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">Conference Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-gray-600">Topic:</span>
                      <p className="text-gray-900">{selectedConference.topic}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Date & Time:</span>
                      <p className="text-gray-900">{selectedConference.conferenceDate} at {selectedConference.conferenceTime} {selectedConference.timePeriod}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Location:</span>
                      <p className="text-gray-900">{selectedConference.city}, {selectedConference.state}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">Venue:</span>
                      <p className="text-gray-900">{selectedConference.place}</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <span className="font-medium text-gray-600">Purpose:</span>
                    <p className="text-gray-900">{selectedConference.purpose}</p>
                  </div>
                </div>

                {/* Accepted Reporters List */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-3">
                    Accepted Reporters ({selectedConference.acceptedReporters?.length || 0})
                  </h4>

                  {(!selectedConference.acceptedReporters || selectedConference.acceptedReporters.length === 0) ? (
                    <p className="text-gray-500 text-center py-4">No reporters have accepted this conference yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Reporter
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              IINSAF ID
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Location
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Accepted At
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Proof Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedConference.acceptedReporters.map((reporter, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {reporter.reporterName}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {reporter.reporterEmail}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {reporter.iinsafId}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {reporter.reporterCity}, {reporter.reporterState}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(reporter.acceptedAt).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {reporter.proofDetails?.adminRejectNote ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    <FaTimes className="mr-1" />
                                    Rejected
                                  </span>
                                ) : reporter.proofSubmitted ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <FaCheckCircle className="mr-1" />
                                    Submitted
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    <FaClock className="mr-1" />
                                    Pending
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proof Review Modal */}
      {showProofModal && selectedProof && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowProofModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Review Submitted Proofs - {selectedProof.conferenceId}
                  </h3>
                  <button
                    onClick={() => setShowProofModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  {selectedProof.acceptedReporters
                    ?.filter(reporter => reporter.proofSubmitted)
                    .map((reporter, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="text-lg font-medium text-gray-900">
                              {reporter.reporterName}
                            </h4>
                            <p className="text-sm text-gray-500">
                              IINSAF ID: {reporter.iinsafId}
                            </p>
                            <p className="text-sm text-gray-500">
                              {reporter.reporterEmail}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">
                              Submitted: {new Date(reporter.proofDetails?.submittedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Video Link</label>
                            <div className="mt-1 flex items-center">
                              <FaYoutube className="w-5 h-5 text-red-600 mr-2" />
                              <a
                                href={reporter.proofDetails?.videoLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 flex items-center"
                              >
                                {reporter.proofDetails?.videoLink}
                                <FaExternalLinkAlt className="w-3 h-3 ml-1" />
                              </a>
                            </div>
                          </div>

                          {reporter.proofDetails?.channelName && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Channel Name</label>
                              <p className="mt-1 text-gray-900">{reporter.proofDetails.channelName}</p>
                            </div>
                          )}

                          {/* Screenshot Display */}
                          {reporter.proofDetails?.screenshot && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Screenshot</label>
                              <div className="mt-1">
                                <img
                                  src={reporter.proofDetails.screenshot.startsWith('http')
                                    ? reporter.proofDetails.screenshot
                                    : `${process.env.REACT_APP_API_BASE_URL}/uploads/conference-proofs/${reporter.proofDetails.screenshot.replace('\\', '/')}`}
                                  alt="Proof Screenshot"
                                  className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-200"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                  onClick={() => {
                                    const imageUrl = reporter.proofDetails.screenshot.startsWith('http')
                                      ? reporter.proofDetails.screenshot
                                      : `${process.env.REACT_APP_API_BASE_URL}/uploads/conference-proofs/${reporter.proofDetails.screenshot.replace('\\', '/')}`;
                                    window.open(imageUrl, '_blank');
                                  }}
                                />
                                <div className="hidden w-full max-w-md h-48 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                                  <div className="text-center">
                                    <FaTimes className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500">Image not available</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Show admin rejection note if proof was rejected */}
                          {reporter.proofDetails?.adminRejectNote && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                              <div className="flex items-start space-x-2">
                                <FaTimes className="text-red-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <h5 className="text-sm font-medium text-red-800">Admin Rejection Note:</h5>
                                  <p className="text-sm text-red-700 mt-1">{reporter.proofDetails.adminRejectNote}</p>
                                  <p className="text-xs text-red-600 mt-1">
                                    Rejected on: {reporter.proofDetails.rejectedAt ? new Date(reporter.proofDetails.rejectedAt).toLocaleDateString() : 'N/A'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Only show action buttons if proof is submitted and not rejected */}
                          {reporter.proofSubmitted && !reporter.proofDetails?.adminRejectNote && (
                            <div className="flex justify-end space-x-3">
                              <button
                                onClick={() => openRejectModal(selectedProof.conferenceId, reporter.reporterId, reporter.reporterName)}
                                disabled={actionLoading}
                                className="px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                              >
                                {actionLoading ? "Processing..." : "Reject"}
                              </button>
                              <button
                                onClick={() => handleApproveProof(selectedProof.conferenceId, reporter.reporterId)}
                                disabled={actionLoading}
                                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                              >
                                {actionLoading ? "Processing..." : "Approve"}
                              </button>
                            </div>
                          )}

                          {/* Show status if proof is rejected */}
                          {reporter.proofDetails?.adminRejectNote && (
                            <div className="flex justify-end">
                              <span className="px-3 py-2 bg-red-100 text-red-800 rounded-md text-sm font-medium">
                                Proof Rejected - Waiting for Resubmission
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedReporter && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowRejectModal(false)}></div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Reject Proof Submission
                  </h3>
                  <button
                    onClick={() => setShowRejectModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FaTimes className="w-6 h-6" />
                  </button>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Reporter: <span className="font-medium">{selectedReporter.reporterName}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Conference ID: <span className="font-medium">{selectedReporter.conferenceId}</span>
                  </p>
                </div>

                <div className="mb-6">
                  <label htmlFor="rejectNote" className="block text-sm font-medium text-gray-700 mb-2">
                    Rejection Reason *
                  </label>
                  <textarea
                    id="rejectNote"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Please provide a detailed reason for rejecting this proof submission..."
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This note will be visible to the reporter and help them understand what needs to be improved.
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowRejectModal(false);
                      setRejectNote("");
                      setSelectedReporter(null);
                    }}
                    disabled={actionLoading}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleRejectProof(selectedReporter.conferenceId, selectedReporter.reporterId)}
                    disabled={actionLoading || !rejectNote.trim()}
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {actionLoading ? "Processing..." : "Reject Proof"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
};

export default RunningFreeConference;
